import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS, toAuditorPortalPageUrl } from "../../config/api";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import { GovernanceListingSection } from "../../components/governance/GovernanceListingSection";
import { ComplianceCenterMetricTile } from "../../components/compliance-center/ComplianceCenterMetricTile";
import { Button } from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import { TextField } from "../../components/ui/TextField";
import { FilePicker } from "../../components/ui/FilePicker";
import ControlEvidenceBoard, {
  type ControlEvidenceRow,
  type LinkedEvidenceItem,
} from "../../components/certification/ControlEvidenceBoard";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";

const TABS = [
  "overview",
  "baseline",
  "scope",
  "controls",
  "evidence",
  "evidenceRequests",
  "findings",
  "remediation",
  "auditors",
  "package",
  "decision",
  "timeline",
] as const;

type TabKey = (typeof TABS)[number];

export default function ComplianceCenterCertificationWorkflowDetail() {
  const { workflowId = "" } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState<TabKey>("overview");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<any>(null);
  const [workflowRecord, setWorkflowRecord] = useState<any>(null);
  const [baseline, setBaseline] = useState<any>(null);
  const [baselineCandidates, setBaselineCandidates] = useState<any>(null);
  const [scope, setScope] = useState<any>(null);
  const [manifest, setManifest] = useState<any>(null);
  const [findings, setFindings] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [pilotReadiness, setPilotReadiness] = useState<any>(null);
  const [informationSystems, setInformationSystems] = useState<any[]>([]);
  const [controlReviews, setControlReviews] = useState<any[]>([]);
  const [evidenceFiles, setEvidenceFiles] = useState<Record<string, File | null>>({});
  const [scopeDraftIds, setScopeDraftIds] = useState<string[]>([]);
  const [baselineDraft, setBaselineDraft] = useState<any>({
    baselineMode: "LATEST_COMPLETED_ASSESSMENT",
    assessmentSessionId: "",
    previousCertificationWorkflowId: "",
  });
  const [invitationDraft, setInvitationDraft] = useState<any>({
    auditorEmail: "",
    auditorName: "",
    auditorRole: "LEAD_AUDITOR",
    invitationLocale: "en",
  });
  const [evidenceRequests, setEvidenceRequests] = useState<any[]>([]);
  const [hubEvidence, setHubEvidence] = useState<any[]>([]);
  const [linkedEvidenceDraft, setLinkedEvidenceDraft] = useState<Record<string, string>>({});
  const [unavailableDraft, setUnavailableDraft] = useState<Record<string, boolean>>({});
  const [legacyLinkDraft, setLegacyLinkDraft] = useState<Record<string, string>>({});
  const [evidenceResponseDraft, setEvidenceResponseDraft] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [lastPortalUrl, setLastPortalUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token],
  );
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const load = useCallback(async () => {
    if (!token || !workflowId) return;
    setLoading(true);
    setError(null);
    try {
      const [
        workflowRes,
        detailRes,
        baselineRes,
        baselineCandidatesRes,
        scopeRes,
        manifestRes,
        findingsRes,
        readinessRes,
        timelineRes,
        systemsRes,
        controlReviewsRes,
        evidenceRequestsRes,
        hubEvidenceRes,
      ] = await Promise.all([
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_DETAIL(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_BASELINE(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_BASELINE_CANDIDATES(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_SCOPE(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_MANIFEST(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_FINDINGS(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_PILOT_READINESS(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_TIMELINE(workflowId), { headers }),
        fetch(API_ENDPOINTS.INFORMATION_SYSTEMS, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_CONTROL_REVIEWS(workflowId), { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_REQUESTS(workflowId), { headers }),
        fetch(API_ENDPOINTS.GOVERNANCE_EVIDENCE, { headers }),
      ]);
      const workflowJson = await workflowRes.json().catch(() => ({}));
      const detailJson = await detailRes.json().catch(() => ({}));
      const baselineJson = await baselineRes.json().catch(() => ({}));
      const baselineCandidatesJson = await baselineCandidatesRes.json().catch(() => ({}));
      const scopeJson = await scopeRes.json().catch(() => ({}));
      const manifestJson = await manifestRes.json().catch(() => ({}));
      const findingsJson = await findingsRes.json().catch(() => ({}));
      const readinessJson = await readinessRes.json().catch(() => ({}));
      const timelineJson = await timelineRes.json().catch(() => ({}));
      const systemsJson = await systemsRes.json().catch(() => ({}));
      const controlReviewsJson = await controlReviewsRes.json().catch(() => ({}));
      const evidenceRequestsJson = await evidenceRequestsRes.json().catch(() => ({}));
      const hubEvidenceJson = await hubEvidenceRes.json().catch(() => ({}));
      setWorkflowRecord(workflowJson?.data || null);
      setDetail(detailJson?.data || null);
      setBaseline(baselineJson?.data || null);
      setBaselineCandidates(baselineCandidatesJson?.data || null);
      setScope(scopeJson?.data || null);
      setManifest(manifestJson?.data || null);
      setFindings(Array.isArray(findingsJson?.data) ? findingsJson.data : []);
      setPilotReadiness(readinessJson?.data || null);
      setTimeline(Array.isArray(timelineJson?.data) ? timelineJson.data : []);
      const systemsPayload = systemsJson?.data;
      const listedSystems = Array.isArray(systemsPayload)
        ? systemsPayload
        : Array.isArray(systemsPayload?.informationSystems)
          ? systemsPayload.informationSystems
          : [];
      const snapshotSystems = Array.isArray(scopeJson?.data?.activeSnapshot?.informationSystems)
        ? scopeJson.data.activeSnapshot.informationSystems
        : [];
      const mergedSystems = listedSystems.length ? listedSystems : snapshotSystems;
      setInformationSystems(mergedSystems);
      setControlReviews(Array.isArray(controlReviewsJson?.data) ? controlReviewsJson.data : []);
      const listedRequests = Array.isArray(evidenceRequestsJson?.data)
        ? evidenceRequestsJson.data
        : (Array.isArray(detailJson?.data?.evidenceRequests) ? detailJson.data.evidenceRequests : []);
      setEvidenceRequests(listedRequests);
      const hubPayload = hubEvidenceJson?.data;
      setHubEvidence(Array.isArray(hubPayload) ? hubPayload : (Array.isArray(hubPayload?.items) ? hubPayload.items : []));
      const scopedIds = Array.isArray(scopeJson?.data?.informationSystemIds)
        ? scopeJson.data.informationSystemIds.map(String)
        : snapshotSystems.map((row: any) => String(row.id)).filter(Boolean);
      setScopeDraftIds(scopedIds);
      setBaselineDraft({
        baselineMode: baselineJson?.data?.baselineMode || "LATEST_COMPLETED_ASSESSMENT",
        assessmentSessionId: baselineJson?.data?.assessmentSessionId || "",
        previousCertificationWorkflowId: baselineJson?.data?.previousCertificationWorkflowId || "",
      });
      if (!workflowRes.ok) {
        throw new Error(workflowJson?.message || t("certification.workflowDetail.errors.loadFailed", "Failed to load workflow detail"));
      }
      if (!detailRes.ok) {
        setError(detailJson?.message || t("certification.workflowDetail.errors.partialLoad", "Workflow loaded, but some certification details are unavailable."));
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("certification.workflowDetail.errors.loadFailed", "Failed to load workflow detail"));
    } finally {
      setLoading(false);
    }
  }, [headers, token, workflowId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = async (key: string, handler: () => Promise<Response | void>, successKey: string) => {
    setActionLoading(key);
    setActionError(null);
    setActionSuccess(null);
    try {
      const response = await handler();
      if (response && !response.ok) {
        const json = await response.json().catch(() => ({}));
        const code = String(json?.code || "");
        if (code === "CERTIFICATION_WORKFLOW_IMMUTABLE") {
          const stage = String(json?.diagnostics?.stage || json?.stage || "");
          throw new Error(
            stage
              ? t(
                "certification.workflowDetail.errors.immutableWithStage",
                "This certification workflow can no longer be changed (stage: {{stage}}).",
              ).replace("{{stage}}", stage)
              : t("certification.workflowDetail.errors.immutable", "This certification workflow can no longer be changed."),
          );
        }
        if (code === "BASELINE_SESSION_REQUIRED") {
          throw new Error(t("certification.workflowDetail.errors.sessionRequired", "Select an assessment session before approving the baseline."));
        }
        if (code === "BASELINE_NOT_APPROVED") {
          throw new Error(t("certification.workflowDetail.errors.baselineRequired", "Approve the audit baseline before approving the evidence manifest."));
        }
        if (code === "SCOPE_NOT_APPROVED") {
          throw new Error(t("certification.workflowDetail.errors.scopeRequired", "Approve the audit scope before approving the evidence manifest."));
        }
        if (code === "EVIDENCE_MANIFEST_ALREADY_APPROVED") {
          throw new Error(t("certification.workflowDetail.errors.manifestAlreadyApproved", "Evidence manifest is already approved."));
        }
        if (code === "CERTIFICATION_REVIEW_PREREQUISITES") {
          const blockerCodes = Array.isArray(json?.blockers)
            ? json.blockers.map((item: any) => String(item?.code || item)).filter(Boolean)
            : [];
          const packageHint = json?.diagnostics?.packageStatus
            ? ` (package status: ${json.diagnostics.packageStatus})`
            : "";
          throw new Error(
            blockerCodes.length
              ? `${t(
                "certification.workflowDetail.errors.reviewPrerequisites",
                "Complete preparation first: {{blockers}}",
              ).replace("{{blockers}}", blockerCodes.join(", "))}${packageHint}`
              : String(json?.message || t("certification.workflowDetail.errors.actionFailed", "Action failed")),
          );
        }
        throw new Error(String(json?.message || t("certification.workflowDetail.errors.actionFailed", "Action failed")));
      }
      setActionSuccess(t(successKey, "Saved"));
      await load();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : t("certification.workflowDetail.errors.actionFailed", "Action failed"));
    } finally {
      setActionLoading(null);
    }
  };

  const runManifestBuild = () =>
    runAction("manifestBuild", async () => {
      const response = await fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_MANIFEST_BUILD(workflowId), { method: "POST", headers });
      if (!response.ok) return response;
      const json = await response.json().catch(() => ({}));
      const builtStatus = String(json?.data?.status || json?.data?.manifestStatus || "").toUpperCase();
      if (builtStatus === "NO_MANIFEST") {
        return new Response(JSON.stringify({
          message: t(
            "certification.workflowDetail.errors.manifestEmpty",
            "Evidence manifest build produced zero draft items. Open the Evidence tab for build diagnostics.",
          ),
          code: "CERTIFICATION_MANIFEST_NOT_BUILT",
          diagnostics: json?.diagnostics || json?.data?.buildDiagnostics || null,
        }), { status: 422, headers: { "Content-Type": "application/json" } });
      }
      return response;
    }, "certification.workflowDetail.success.manifestBuilt");

  const approveBaseline = () =>
    runAction("baselineApprove", async () => {
      const saved = await fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_BASELINE(workflowId), {
        method: "PATCH",
        headers,
        body: JSON.stringify(baselineDraft),
      });
      if (!saved.ok) return saved;
      return fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_BASELINE_APPROVE(workflowId), { method: "POST", headers });
    }, "certification.workflowDetail.success.baselineApproved");

  const ensureAndGeneratePackage = () =>
    runAction("packageGenerate", async () => {
      let id = packageId;
      if (!id) {
        const created = await fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_PACKAGES, {
          method: "POST",
          headers,
          body: JSON.stringify({
            name: `${workflow?.name || "Certification"} package`,
            standardCode: workflow?.standardCode || "SOC2",
          }),
        });
        const createdJson = await created.json().catch(() => ({}));
        if (!created.ok) return new Response(JSON.stringify(createdJson), { status: created.status, headers: { "Content-Type": "application/json" } });
        id = createdJson?.data?.id;
        if (id) {
          await fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW(workflowId), {
            method: "PATCH",
            headers,
            body: JSON.stringify({ certificationPackageId: id }),
          });
        }
      }
      if (!id) {
        return new Response(JSON.stringify({ message: t("certification.workflowDetail.errors.packageMissing", "Package could not be created") }), { status: 400, headers: { "Content-Type": "application/json" } });
      }
      return fetch(`${API_ENDPOINTS.COMPLIANCE_CERTIFICATION_PACKAGES}/${id}/generate`, { method: "POST", headers });
    }, "certification.workflowDetail.success.packageGenerated");

  const workflow = detail?.workflow || workflowRecord || null;
  const lifecycle = workflowRecord?.lifecycle || workflow?.lifecycle || null;
  const lifecycleStage = String(lifecycle?.stage || "PREPARATION");
  const terminal = Boolean(lifecycle?.terminal);
  const allowedActions = Array.isArray(lifecycle?.allowedActions) ? lifecycle.allowedActions : [];
  const reviewBlockers = Array.isArray(lifecycle?.blockers)
    ? lifecycle.blockers
    : (Array.isArray(pilotReadiness?.blockers) ? pilotReadiness.blockers : []);
  const preparationBlockers = reviewBlockers.filter((blocker: any) => {
    const code = String(blocker?.code || blocker || "");
    return code !== "VALID_AUDITOR_TOKEN_MISSING";
  });
  const inviteBlockers = preparationBlockers.filter((blocker: any) => {
    const code = String(blocker?.code || blocker || "");
    return code !== "PACKAGE_NOT_FROZEN";
  });
  const selectedModeRequiresSession = baselineDraft.baselineMode === "SELECTED_ASSESSMENT_SESSION";
  const baselineSessionSelected = Boolean(baselineDraft.assessmentSessionId);
  const baselineApproved = Boolean(
    baseline?.approved
    || baseline?.approvedAt
    || Number(baseline?.baselineVersion || 0) >= 1,
  );
  const manifestExists = Number(manifest?.evidenceManifestVersion || 0) > 0 || Boolean((manifest?.items || []).length);
  const manifestApproved = Boolean(manifest?.approvedAt || manifest?.approved);
  const manifestStatus = String(manifest?.status || (manifestApproved ? "APPROVED" : ((manifest?.items || []).length ? "DRAFT" : "NO_MANIFEST")));
  const manifestCandidateCount = Number(manifest?.candidateCount || (manifest?.items || []).length || 0);
  const manifestIncludedCount = Number(manifest?.includedCount || (manifest?.items || []).filter((item: any) => item?.included !== false).length || 0);
  const manifestExcludedCount = Number(manifest?.excludedCount || (manifest?.items || []).filter((item: any) => item?.included === false).length || 0);
  const manifestApprovedCount = Number(manifest?.approvedCount || (manifest?.items || []).filter((item: any) => item?.disclosureApproved === true).length || 0);
  const scopeApproved = Boolean(scope?.activeSnapshot && Number(scope?.scopeVersion || 0) >= 1);
  const packageId = workflowRecord?.certificationPackageId
    || workflowRecord?.workflow?.certificationPackageId
    || workflowRecord?.packageRecord?.id
    || detail?.workflow?.certificationPackageId
    || null;
  const packageStatus = String(
    workflowRecord?.packageRecord?.status
    || detail?.workflow?.packageRecord?.status
    || workflowRecord?.metadata?.certificationPackageStatus
    || "",
  ).toUpperCase();
  const packageFrozen = packageStatus === "FROZEN" || packageStatus === "APPROVED" || Boolean(workflowRecord?.packageRecord?.frozenAt);
  const canInviteAuditor = Boolean(invitationDraft.auditorEmail)
    && inviteBlockers.length === 0
    && Boolean(packageId)
    && manifestApproved
    && baselineApproved
    && scopeApproved;
  const canApproveBaseline = allowedActions.includes("BASELINE_APPROVE") && !baselineApproved;
  const canApproveScope = allowedActions.includes("SCOPE_APPROVE") && !scopeApproved;
  const canBuildManifest = allowedActions.includes("MANIFEST_BUILD") && baselineApproved && scopeApproved && !manifestApproved;
  const canApproveManifest = allowedActions.includes("MANIFEST_APPROVE") && manifestStatus === "DRAFT" && manifestIncludedCount > 0;
  const canGeneratePackage = allowedActions.includes("PACKAGE_GENERATE") && manifestApproved;
  const canFreezePackage = allowedActions.includes("PACKAGE_FREEZE")
    && Boolean(packageId)
    && Boolean(manifest?.packageVersion)
    && !packageFrozen;
  const packagePrepared = Boolean(manifest?.packageVersion);
  const nextBlockerCode = String(reviewBlockers[0]?.code || reviewBlockers[0] || "");
  const freezeIsNextStep = nextBlockerCode === "PACKAGE_NOT_FROZEN" || nextBlockerCode === "PACKAGE_NOT_GENERATED";
  const packageFreezeBlockers = [
    !allowedActions.includes("PACKAGE_FREEZE") ? t("certification.workflowDetail.blockers.stage", "Workflow stage does not allow package freeze") : null,
    !packageId ? t("certification.workflowDetail.blockers.PACKAGE_MISSING", "Certification package is missing") : null,
    !manifest?.packageVersion ? t("certification.workflowDetail.blockers.PACKAGE_NOT_GENERATED", "Approve the evidence manifest before freezing the package") : null,
    packageFrozen ? t("certification.workflowDetail.success.packageFrozen", "Package frozen") : null,
  ].filter(Boolean);
  const canComplete = allowedActions.includes("COMPLETE_WORKFLOW") && !terminal;
  const openFindings = findings.filter((item) => String(item.status || "").toUpperCase() !== "CLOSED").length;
  const openEvidenceRequests = evidenceRequests.filter((item: any) => {
    const status = String(item.status || "").toLowerCase();
    return status !== "closed" && status !== "accepted" && status !== "rejected";
  }).length;
  const reviewedControls = controlReviews.filter(
    (item) => item.auditorReviewStatus && String(item.auditorReviewStatus).toUpperCase() !== "NOT_REVIEWED",
  ).length;
  const linkedRemediation = findings.filter((f) => Boolean(f.remediationId)).length;

  const selectedSessionCandidates = Array.isArray(baselineCandidates?.assessmentSessions)
    ? baselineCandidates.assessmentSessions
    : [];
  const selectedWorkflowCandidates = Array.isArray(baselineCandidates?.previousCertificationWorkflows)
    ? baselineCandidates.previousCertificationWorkflows.filter((row: any) => String(row?.id) !== String(workflowId))
    : [];
  const assessmentSnapshotDiagnostics = detail?.assessmentSnapshotDiagnostics || null;
  const manifestBuildDiagnostics = detail?.manifestBuildDiagnostics || null;
  const snapshotManifestCandidateCount = Number(assessmentSnapshotDiagnostics?.manifestCandidates || 0);
  const manifestBuildAttempted = Boolean(manifestBuildDiagnostics?.lastBuildAttemptAt);
  const manifestBuildBlockers = [
    !allowedActions.includes("MANIFEST_BUILD") ? t("certification.workflowDetail.blockers.stage", "Workflow stage does not allow manifest build") : null,
    !baselineApproved ? t("certification.workflowDetail.blockers.baseline", "Baseline not approved") : null,
    !scopeApproved ? t("certification.workflowDetail.blockers.scope", "Scope not approved") : null,
    manifestApproved ? t("certification.workflowDetail.blockers.manifestApproved", "Manifest already approved") : null,
  ].filter(Boolean);
  const manifestApproveBlockers = [
    !allowedActions.includes("MANIFEST_APPROVE") ? t("certification.workflowDetail.blockers.stage", "Workflow stage does not allow manifest approval") : null,
    manifestApproved ? t("certification.workflowDetail.blockers.manifestApproved", "Manifest already approved") : null,
    manifestStatus !== "DRAFT" ? t("certification.workflowDetail.blockers.manifestNotDraft", "Draft manifest is required before approval") : null,
    manifestIncludedCount <= 0 ? t("certification.workflowDetail.blockers.manifestEmpty", "At least one included manifest item is required") : null,
  ].filter(Boolean);
  const lifecycleLabel = t(`certification.status.lifecycle.${lifecycleStage}`, lifecycleStage.replaceAll("_", " "));
  const formatOrganizationStatus = (value?: string | null) => {
    const normalized = String(value || "UNASSESSED").toUpperCase();
    if (normalized === "COMPLIANT") return "Compliant / YES";
    if (normalized === "NON_COMPLIANT") return "Non-compliant / NO";
    if (normalized === "PENDING_EVIDENCE") return "Pending evidence";
    if (normalized === "NOT_APPLICABLE") return "Not applicable";
    if (normalized === "PARTIAL") return "Partial";
    return "Unassessed";
  };

  const controlEvidenceRows = useMemo<ControlEvidenceRow[]>(() => {
    const byId = new Map<string, LinkedEvidenceItem>();
    for (const ev of hubEvidence) {
      if (!ev?.id) continue;
      byId.set(String(ev.id), {
        id: String(ev.id),
        title: ev.title || null,
        description: ev.description || null,
        verificationStatus: ev.verificationStatus || null,
        evidenceType: ev.evidenceType || ev.artifactCategory || null,
        artifactCategory: ev.artifactCategory || null,
        hasAttachment: Boolean(ev.filePath || ev.hasAttachment || ev.fileName),
        fileName: ev.fileName || null,
      });
    }
    for (const item of manifest?.items || []) {
      const evidenceId = item?.evidenceId ? String(item.evidenceId) : "";
      if (!evidenceId) continue;
      const prev = byId.get(evidenceId) || { id: evidenceId };
      byId.set(evidenceId, {
        ...prev,
        id: evidenceId,
        title: prev.title || item.title || null,
        verificationStatus: prev.verificationStatus || item.verificationStatus || null,
        evidenceType: prev.evidenceType || item.evidenceType || item.type || null,
        hasAttachment: true,
        fileName: prev.fileName || null,
      });
    }
    for (const req of evidenceRequests) {
      const linked = req?.linkedEvidence;
      if (linked?.id) {
        byId.set(String(linked.id), {
          id: String(linked.id),
          title: linked.title || req.uploadedFileName || null,
          fileName: linked.fileName || req.uploadedFileName || null,
          hasAttachment: Boolean(linked.hasAttachment ?? linked.fileName),
          verificationStatus: linked.verificationStatus || null,
          evidenceType: linked.evidenceType || linked.artifactCategory || null,
        });
      }
    }

    return controlReviews.map((item: any) => {
      const key = String(item.controlKey || "");
      const ids = Array.isArray(item.linkedEvidenceIds) ? item.linkedEvidenceIds.map(String) : [];
      const fromApi = Array.isArray(item.linkedEvidence) ? item.linkedEvidence : [];
      const merged = new Map<string, LinkedEvidenceItem>();
      for (const row of fromApi) {
        if (row?.id) merged.set(String(row.id), row);
      }
      for (const id of ids) {
        if (!merged.has(id) && byId.has(id)) merged.set(id, byId.get(id)!);
      }
      for (const req of evidenceRequests) {
        const reqControl = String(req.nativeControlKey || req.controlCode || req?.metadata?.nativeControlKey || "");
        if (reqControl !== key) continue;
        const linked = req.linkedEvidence;
        if (linked?.id) {
          merged.set(String(linked.id), {
            id: String(linked.id),
            title: linked.title || req.title || null,
            fileName: linked.fileName || req.uploadedFileName || null,
            hasAttachment: Boolean(linked.hasAttachment ?? linked.fileName),
            verificationStatus: linked.verificationStatus || null,
            evidenceType: linked.evidenceType || null,
          });
        }
      }
      for (const man of manifest?.items || []) {
        const manControl = String(man.nativeControlKey || man.controlKey || "");
        if (manControl !== key || !man.evidenceId) continue;
        const id = String(man.evidenceId);
        if (!merged.has(id)) {
          merged.set(id, byId.get(id) || {
            id,
            title: man.title || id,
            hasAttachment: true,
            verificationStatus: man.verificationStatus || null,
            evidenceType: man.evidenceType || man.type || null,
          });
        }
      }
      const linkedEvidence = Array.from(merged.values());
      const openRequestCount = evidenceRequests.filter((req: any) => {
        const reqControl = String(req.nativeControlKey || req.controlCode || req?.metadata?.nativeControlKey || "");
        const status = String(req.status || "").toLowerCase();
        return reqControl === key && status !== "closed" && status !== "accepted" && status !== "rejected";
      }).length;
      const findingCount = findings.filter((f) => String(f.controlKey || "") === key).length;
      return {
        controlKey: key,
        title: item.title || null,
        criteriaReference: item.criteriaReference || null,
        organizationSelfAssessment: item.organizationSelfAssessment || null,
        organizationReason: item.organizationReason || null,
        auditorReviewStatus: item.auditorReviewStatus || null,
        auditorComment: item.auditorComment || null,
        linkedEvidenceCount: linkedEvidence.length,
        linkedEvidence,
        openRequestCount,
        findingCount,
        preview: Boolean(item.preview),
      };
    });
  }, [controlReviews, hubEvidence, evidenceRequests, findings, manifest]);

  const downloadOrgEvidence = async (evidenceId: string, fileName?: string | null) => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_EVIDENCE_DOWNLOAD(evidenceId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `evidence-${evidenceId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      setActionError(t("governance.evidence.downloadError", "Failed to download evidence"));
    }
  };
  const formatSessionLabel = (candidate: any) => {
    if (candidate?.label) return String(candidate.label);
    const date = String(candidate.completedAt || candidate.createdAt || "").slice(0, 10) || t("certification.workflowDetail.none", "None");
    const cycle = candidate.assessmentCycle ? `${t("certification.workflowDetail.sessionCycle", "Cycle")} ${candidate.assessmentCycle}` : "";
    const version = candidate.assessmentVersion ? `${t("certification.workflowDetail.sessionVersion", "Version")} ${candidate.assessmentVersion}` : "";
    const status = t(`certification.status.assessment.${String(candidate.lifecycleStatus || candidate.status || "COMPLETED").toUpperCase()}`, String(candidate.lifecycleStatus || candidate.status || "completed"));
    return [date, cycle, version, status].filter(Boolean).join(" · ");
  };
  const selectedSystems = informationSystems.filter((system) => scopeDraftIds.includes(String(system.id)));
  const availableSystems = informationSystems.filter((system) => !scopeDraftIds.includes(String(system.id)));
  const derivedAssetCount = selectedSystems.reduce((sum, system) => sum + Number(system.linkedAssetsCount || system.assetCount || 0), 0);
  const eventTypeLabel = (value?: string | null) =>
    t(`certification.status.event.${String(value || "").toUpperCase()}`, String(value || "").replaceAll("_", " "));

  const renderMessage = () => (
    <>
      {actionError ? <p className={listStyles.panelSubtitle}>{actionError}</p> : null}
      {actionSuccess ? <p className={listStyles.panelSubtitle}>{actionSuccess}</p> : null}
    </>
  );

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={workflow?.name || t("certification.workflowDetail.title", "Certification workflow")}
        description={workflow?.standardCode || ""}
        actions={
          <Button type="button" size="small" color="outline" onClick={() => navigate("/compliance-center/certification")}>
            {t("common.back", "Back")}
          </Button>
        }
      >
        {loading ? <p className={listStyles.panelSubtitle}>{t("common.loading", "Loading...")}</p> : null}
        {error ? <p className={listStyles.panelSubtitle}>{error}</p> : null}
        {!loading && workflow ? (
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
            <ComplianceCenterMetricTile label={t("certification.workflowDetail.workflowStatus", "Workflow status")} value={lifecycleLabel} tone="blue" />
            <ComplianceCenterMetricTile label={t("certification.workflowDetail.openEvidenceRequests", "Open evidence requests")} value={openEvidenceRequests} tone="orange" />
            <ComplianceCenterMetricTile label={t("certification.workflowDetail.openFindings", "Open findings")} value={openFindings} tone="purple" />
            <ComplianceCenterMetricTile label={t("certification.workflowDetail.reviewProgress", "Controls reviewed")} value={reviewedControls} tone="green" />
          </div>
        ) : null}
      </GovernanceListingSection>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {TABS.map((key) => (
          <Button key={key} type="button" size="small" color={tab === key ? "primary" : "secondary"} onClick={() => { setTab(key); setActionError(null); setActionSuccess(null); }}>
            {t(
              `certification.workflowDetail.tabs.${key}`,
              key === "controls"
                ? "Controls & Evidence"
                : key === "evidence"
                  ? "Evidence package"
                  : key,
            )}
          </Button>
        ))}
      </div>

      {tab === "overview" && workflow ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.overview", "Overview")}>
          {renderMessage()}
          <p>{t("certification.workflowDetail.lifecycleStatus", "Lifecycle")}: {lifecycleLabel}</p>
          <p>{t("certification.workflowDetail.scopeVersion", "Scope version")}: {scope?.scopeVersion || 0}</p>
          <p>{t("certification.workflowDetail.baselineVersion", "Baseline version")}: {baseline?.baselineVersion || 0}</p>
          <p>{t("certification.workflowDetail.packageVersion", "Package version")}: {manifest?.packageVersion || t("certification.workflowDetail.notPrepared", "Not prepared")}</p>
          <p>{t("certification.workflowDetail.manifestVersion", "Evidence manifest version")}: {manifest?.evidenceManifestVersion || 0}</p>
          <p>{t("certification.workflowDetail.auditorCount", "Auditors")}: {(detail?.auditors || []).length}</p>
          <p>{t("certification.workflowDetail.linkedRemediation", "Linked remediation")}: {linkedRemediation}</p>
          <p>{t("certification.workflowDetail.decisionStatus", "Decision status")}: {detail?.decisions?.[0]?.decision ? t(`certification.status.decision.${String(detail.decisions[0].decision).toUpperCase()}`, String(detail.decisions[0].decision).replaceAll("_", " ")) : "—"}</p>
          {reviewBlockers.length ? (
            <div className={listStyles.panelCard}>
              <strong>{t("certification.workflowDetail.preparationRequired", "Preparation required")}</strong>
              <ol>
                {reviewBlockers.map((blocker: any, index: number) => {
                  const code = String(blocker.code || blocker);
                  return (
                    <li key={`${code}-${index}`}>
                      <Button type="button" size="small" color="outline" onClick={() => setTab((blocker.tab || "overview") as TabKey)}>
                        {index + 1}. {t(`certification.workflowDetail.blockers.${code}`, code.replaceAll("_", " ").toLowerCase())}
                      </Button>
                    </li>
                  );
                })}
              </ol>
            </div>
          ) : (
            <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.ready", "Workflow is pilot-ready.")}</p>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button type="button" size="small" color="primary" disabled={actionLoading !== null || terminal || !canApproveBaseline || (selectedModeRequiresSession && !baselineSessionSelected)} onClick={() => void approveBaseline()}>
              {actionLoading === "baselineApprove" ? t("common.saving", "Saving...") : t("certification.workflowDetail.approveBaseline", "Approve baseline")}
            </Button>
            <Button type="button" size="small" color="secondary" disabled={actionLoading !== null || terminal || !canApproveScope} onClick={() => void runAction("scopeApprove", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_SCOPE_APPROVE(workflowId), { method: "POST", headers }), "certification.workflowDetail.success.scopeApproved")}>
              {actionLoading === "scopeApprove" ? t("common.saving", "Saving...") : t("certification.workflowDetail.approveScope", "Approve scope")}
            </Button>
            <Button type="button" size="small" color="secondary" disabled={actionLoading !== null || terminal || !canBuildManifest} onClick={() => void runManifestBuild()}>
              {actionLoading === "manifestBuild" ? t("common.generating", "Generating...") : t("certification.workflowDetail.buildManifest", "Build evidence manifest")}
            </Button>
            <Button type="button" size="small" color="secondary" disabled={actionLoading !== null || terminal || !canApproveManifest} onClick={() => void runAction("manifestApprove", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_MANIFEST_APPROVE(workflowId), { method: "POST", headers }), "certification.workflowDetail.success.manifestApproved")}>
              {actionLoading === "manifestApprove" ? t("common.saving", "Saving...") : t("certification.workflowDetail.approveManifest", "Approve evidence manifest")}
            </Button>
            <Button type="button" size="small" color={freezeIsNextStep ? "primary" : "secondary"} disabled={actionLoading !== null || terminal || !canFreezePackage} onClick={() => void runAction("packageFreeze", () => fetch(`${API_ENDPOINTS.COMPLIANCE_CERTIFICATION_PACKAGES}/${packageId}/freeze`, { method: "POST", headers }), "certification.workflowDetail.success.packageFrozen")}>
              {actionLoading === "packageFreeze" ? t("common.saving", "Saving...") : t("certification.workflowDetail.freezePackage", "Freeze package")}
            </Button>
            {!canFreezePackage && freezeIsNextStep && packageFreezeBlockers.length ? (
              <span className={listStyles.panelSubtitle}>{packageFreezeBlockers.join(" · ")}</span>
            ) : null}
            <Button type="button" size="small" color="primary" disabled={actionLoading !== null || terminal || reviewBlockers.length > 0} onClick={() => void runAction("submit", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_SUBMIT(workflowId), { method: "POST", headers }), "certification.workflowDetail.success.workflowSubmitted")}>
              {actionLoading === "submit" ? t("common.saving", "Saving...") : t("certification.workflowDetail.submitWorkflow", "Submit for auditor review")}
            </Button>
            {canComplete ? (
              <Button type="button" size="small" color="secondary" disabled={actionLoading !== null} onClick={() => void runAction("complete", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_COMPLETE(workflowId), { method: "POST", headers }), "certification.workflowDetail.success.workflowCompleted")}>
                {actionLoading === "complete" ? t("common.saving", "Saving...") : t("certification.workflowDetail.completeWorkflow", "Complete workflow")}
              </Button>
            ) : null}
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === "baseline" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.baseline", "Baseline")}>
          {renderMessage()}
          <p className={listStyles.panelSubtitle}>{t(`certification.workflowDetail.baselineModes.${baseline?.baselineMode === "SELECTED_ASSESSMENT_SESSION" ? "selected" : baseline?.baselineMode === "PREVIOUS_CERTIFICATION_WORKFLOW" ? "previous" : baseline?.baselineMode === "CURRENT_STATE_SNAPSHOT" ? "current" : "latest"}`, "Latest completed assessment")}</p>
          <p>{t("certification.workflowDetail.baselineVersion", "Baseline version")}: {baseline?.baselineVersion || 0}</p>
          <p>{t("certification.workflowDetail.approvedAt", "Approved at")}: {baseline?.approvedAt || "—"}</p>
          <p>{t("certification.workflowDetail.approvedBy", "Approved by")}: {baseline?.approvedBy || "—"}</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, alignItems: "flex-end" }}>
            <Select
              label={t("certification.workflowDetail.baselineMode", "Baseline mode")}
              value={baselineDraft.baselineMode}
              onChange={(value) => setBaselineDraft((prev: any) => ({ ...prev, baselineMode: String(value || "LATEST_COMPLETED_ASSESSMENT") }))}
              disabled={terminal || baselineApproved}
              options={[
                { value: "LATEST_COMPLETED_ASSESSMENT", label: t("certification.workflowDetail.baselineModes.latest", "Latest completed assessment") },
                { value: "SELECTED_ASSESSMENT_SESSION", label: t("certification.workflowDetail.baselineModes.selected", "Selected assessment session") },
                { value: "PREVIOUS_CERTIFICATION_WORKFLOW", label: t("certification.workflowDetail.baselineModes.previous", "Previous certification workflow") },
                { value: "CURRENT_STATE_SNAPSHOT", label: t("certification.workflowDetail.baselineModes.current", "Current state snapshot") },
              ]}
            />
            <Select
              label={t("certification.workflowDetail.assessmentSession", "Assessment session")}
              value={baselineDraft.assessmentSessionId || null}
              onChange={(value) => setBaselineDraft((prev: any) => ({ ...prev, assessmentSessionId: String(value || "") }))}
              placeholder={t("certification.workflowDetail.none", "None")}
              disabled={terminal || baselineApproved}
              options={selectedSessionCandidates.map((candidate: any) => ({
                value: candidate.id,
                label: formatSessionLabel(candidate),
              }))}
            />
            <Select
              label={t("certification.workflowDetail.previousWorkflow", "Previous workflow")}
              value={baselineDraft.previousCertificationWorkflowId || null}
              onChange={(value) => setBaselineDraft((prev: any) => ({ ...prev, previousCertificationWorkflowId: String(value || "") }))}
              placeholder={t("certification.workflowDetail.none", "None")}
              options={selectedWorkflowCandidates.map((candidate: any) => ({
                value: candidate.id,
                label: candidate.name || candidate.id,
              }))}
            />
            <Button type="button" size="small" color="secondary" disabled={actionLoading !== null || terminal || (selectedModeRequiresSession && !baselineSessionSelected)} onClick={() => void runAction("baselineUpdate", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_BASELINE(workflowId), { method: "PATCH", headers, body: JSON.stringify(baselineDraft) }), "certification.workflowDetail.success.baselineSaved")}>
              {actionLoading === "baselineUpdate" ? t("common.saving", "Saving...") : t("certification.workflowDetail.saveBaseline", "Save baseline")}
            </Button>
            <Button type="button" size="small" color="primary" disabled={actionLoading !== null || terminal || (selectedModeRequiresSession && !baselineSessionSelected)} onClick={() => void approveBaseline()}>
              {actionLoading === "baselineApprove" ? t("common.saving", "Saving...") : t("certification.workflowDetail.approveBaseline", "Approve baseline")}
            </Button>
          </div>
          {!selectedSessionCandidates.length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.noAssessmentSessions", "No completed assessment sessions for this standard yet. Complete an assessment, then save and approve the baseline.")}</p> : null}
        </GovernanceListingSection>
      ) : null}

      {tab === "scope" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.scope", "Scope")}>
          {renderMessage()}
          <p>{t("certification.workflowDetail.scopeVersion", "Scope version")}: {scope?.scopeVersion || 0}</p>
          <p>{t("certification.workflowDetail.standard", "Standard")}: {workflow?.standardCode || "—"}</p>
          <p>{t("certification.workflowDetail.derivedAssets", "Derived assets")}: {derivedAssetCount}</p>
          <div className={listStyles.listStack}>
            <div className={listStyles.panelCard}>
              <strong>{t("certification.workflowDetail.selectedSystems", "Selected for audit")}</strong>
              {selectedSystems.map((system) => (
                <label key={`selected-${system.id}`} className={listStyles.panelSubtitle} style={{ display: "block" }}>
                  <input
                    type="checkbox"
                    checked
                    disabled={terminal}
                    onChange={() => setScopeDraftIds((prev) => prev.filter((id) => id !== String(system.id)))}
                  />
                  {" "}
                  {system.name || system.identifier || system.code || system.id}
                  {" · "}
                  {system.identifier || system.id}
                  {" · "}
                  {t("certification.workflowDetail.environment", "Environment")}: {system.environment || "—"}
                  {" · "}
                  {t("certification.workflowDetail.criticality", "Criticality")}: {system.criticality || "—"}
                  {" · "}
                  {t("certification.workflowDetail.assetCount", "Assets")}: {system.linkedAssetsCount ?? system.assetCount ?? 0}
                </label>
              ))}
              {!selectedSystems.length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.noSelectedSystems", "No systems selected for this audit.")}</p> : null}
            </div>
            <div className={listStyles.panelCard}>
              <strong>{t("certification.workflowDetail.availableSystems", "Available systems")}</strong>
              {availableSystems.map((system) => (
                <label key={`available-${system.id}`} className={listStyles.panelSubtitle} style={{ display: "block" }}>
                  <input
                    type="checkbox"
                    checked={false}
                    disabled={terminal}
                    onChange={() => setScopeDraftIds((prev) => [...new Set([...prev, String(system.id)])])}
                  />
                  {" "}
                  {system.name || system.identifier || system.code || system.id}
                  {" · "}
                  {system.identifier || system.id}
                  {" · "}
                  {t("certification.workflowDetail.environment", "Environment")}: {system.environment || "—"}
                  {" · "}
                  {t("certification.workflowDetail.criticality", "Criticality")}: {system.criticality || "—"}
                  {" · "}
                  {t("certification.workflowDetail.assetCount", "Assets")}: {system.linkedAssetsCount ?? system.assetCount ?? 0}
                </label>
              ))}
              {!informationSystems.length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.emptyScopeSystems", "No information systems found. Create systems in Assets, then select them here for the audit scope.")}</p> : null}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Button type="button" size="small" color="secondary" disabled={actionLoading !== null || terminal} onClick={() => void runAction("scopeSave", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_SCOPE(workflowId), { method: "PATCH", headers, body: JSON.stringify({ informationSystemIds: scopeDraftIds }) }), "certification.workflowDetail.success.scopeSaved")}>
              {actionLoading === "scopeSave" ? t("common.saving", "Saving...") : t("certification.workflowDetail.saveScope", "Save scope")}
            </Button>
            <Button type="button" size="small" color="primary" disabled={actionLoading !== null || terminal || !scopeDraftIds.length} onClick={() => void runAction("scopeApproveTab", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_SCOPE_APPROVE(workflowId), { method: "POST", headers }), "certification.workflowDetail.success.scopeApproved")}>
              {actionLoading === "scopeApproveTab" ? t("common.saving", "Saving...") : t("certification.workflowDetail.approveScope", "Approve scope")}
            </Button>
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === "controls" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.controlsEvidence", "Controls & Evidence")}>
          {assessmentSnapshotDiagnostics ? (
            <p className={listStyles.panelSubtitle}>
              Session {assessmentSnapshotDiagnostics.sessionId} · Native controls {assessmentSnapshotDiagnostics.nativeControls} ·
              assessed {assessmentSnapshotDiagnostics.controlsWithOrganizationStatus} · linked evidence {assessmentSnapshotDiagnostics.linkedEvidenceIds} ·
              manifest candidates {assessmentSnapshotDiagnostics.manifestCandidates}
            </p>
          ) : null}
          <ControlEvidenceBoard
            mode="organization"
            controls={controlEvidenceRows}
            emptyLabel={t("certification.workflowDetail.emptyControls", "No control reviews yet. Approve a baseline with an assessment session to load native controls.")}
            formatOrgAssessment={formatOrganizationStatus}
            formatReviewStatus={(value) => t(`certification.status.review.${String(value || "NOT_REVIEWED").toUpperCase()}`, String(value || "NOT_REVIEWED").replaceAll("_", " "))}
            onDownloadEvidence={(id, fileName) => void downloadOrgEvidence(id, fileName)}
            labels={{
              orgAssessment: t("certification.portal.organizationAssessment", "Organization"),
              auditorAssessment: t("certification.portal.auditorAssessment", "Auditor"),
              reason: t("certification.portal.organizationReason", "Reason / comment"),
              evidence: t("certification.workflowDetail.tabs.evidence", "Evidence"),
              noEvidence: t("certification.workflowDetail.noLinkedEvidence", "No linked evidence artifacts yet."),
              openRequests: t("certification.workflowDetail.openEvidenceRequests", "Open evidence requests"),
              findings: t("certification.workflowDetail.tabs.findings", "Findings"),
              filters: t("certification.workflowDetail.filters", "Filters"),
              filterAssessment: t("certification.workflowDetail.filterAssessment", "Assessment status"),
              filterEvidence: t("certification.workflowDetail.filterEvidence", "Evidence presence"),
              filterReview: t("certification.workflowDetail.filterReview", "Auditor review"),
              download: t("certification.portal.downloadEvidence", "Download attachment"),
              loading: t("common.loading", "Loading…"),
            }}
          />
        </GovernanceListingSection>
      ) : null}

      {tab === "evidence" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.evidencePackage", "Evidence package / manifest")}>
          <p className={listStyles.panelSubtitle}>
            {t(
              "certification.workflowDetail.evidencePackageHint",
              "Package disclosure checklist for auditor handoff. Day-to-day control evidence review is on the Controls & Evidence tab.",
            )}
          </p>
          <p>{t("certification.workflowDetail.manifestVersion", "Evidence manifest version")}: {manifest?.evidenceManifestVersion || 0}</p>
          <p>Candidates: {manifestCandidateCount} · Included: {manifestIncludedCount} · Excluded: {manifestExcludedCount} · Disclosure approved: {manifestApprovedCount}</p>
          {snapshotManifestCandidateCount > 0 && manifestCandidateCount === 0 ? (
            <p className={listStyles.panelSubtitle}>
              Assessment snapshot has {snapshotManifestCandidateCount} manifest candidates, but no persisted draft manifest items were loaded.
              {manifestBuildAttempted
                ? ` Last build: ${manifestBuildDiagnostics?.lastBuildResult || "UNKNOWN"} at ${String(manifestBuildDiagnostics?.lastBuildAttemptAt || "").slice(0, 19).replace("T", " ") || "unknown time"}.`
                : " Manifest has not been built yet."}
              {manifestBuildDiagnostics?.lastBuildFailureStage ? ` Failure stage: ${manifestBuildDiagnostics.lastBuildFailureStage}.` : null}
              {manifestBuildDiagnostics?.lastBuildError ? ` Error: ${manifestBuildDiagnostics.lastBuildError}.` : null}
              {manifestBuildAttempted
                ? ` Build saw ${Number(manifestBuildDiagnostics?.snapshotCandidateCount || manifestBuildDiagnostics?.snapshotManifestCandidates || 0)} snapshot candidates.`
                : null}
            </p>
          ) : null}
          {!canBuildManifest && manifestCandidateCount === 0 && snapshotManifestCandidateCount > 0 && manifestBuildBlockers.length ? (
            <p className={listStyles.panelSubtitle}>
              Build is currently blocked: {manifestBuildBlockers.join(" · ")}
            </p>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {canApproveManifest ? (
              <Button
                type="button"
                size="small"
                color="primary"
                disabled={actionLoading !== null || terminal}
                onClick={() => void runAction("manifestApprove", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_MANIFEST_APPROVE(workflowId), { method: "POST", headers }), "certification.workflowDetail.success.manifestApproved")}
              >
                {actionLoading === "manifestApprove" ? t("common.saving", "Saving...") : t("certification.workflowDetail.approveManifest", "Approve evidence manifest")}
              </Button>
            ) : null}
            <Button
              type="button"
              size="small"
              color={canApproveManifest ? "secondary" : "primary"}
              disabled={actionLoading !== null || terminal || !canBuildManifest}
              onClick={() => void runManifestBuild()}
            >
              {actionLoading === "manifestBuild" ? t("common.generating", "Generating...") : t("certification.workflowDetail.buildManifest", "Build evidence manifest")}
            </Button>
            {!canApproveManifest && manifestStatus === "DRAFT" && manifestIncludedCount > 0 && manifestApproveBlockers.length ? (
              <span className={listStyles.panelSubtitle}>{manifestApproveBlockers.join(" · ")}</span>
            ) : null}
            {!canBuildManifest && manifestBuildBlockers.length ? (
              <span className={listStyles.panelSubtitle}>{manifestBuildBlockers.join(" · ")}</span>
            ) : null}
          </div>
          {manifestCandidateCount > 0 && !(manifest?.items || []).length ? (
            <p className={listStyles.panelSubtitle}>
              Manifest candidate count is non-zero but item payload is empty. Reload workflow detail; if this persists, inspect certification manifest persistence diagnostics.
            </p>
          ) : null}
          {manifest?.readbackSource === "package-fallback" ? (
            <p className={listStyles.panelSubtitle}>
              Draft manifest items were recovered from the certification package because workflow metadata did not contain draft items.
            </p>
          ) : null}
          <div className={listStyles.listStack}>
            {(manifest?.items || []).map((item: any) => (
              <div key={item.packageItemId || `${item.evidenceId || "no-evidence"}-${item.questionKey || item.questionId || "no-question"}-${item.controlKey || item.nativeControlKey || "no-control"}`} className={listStyles.panelCard}>
                <strong>{item.title || item.evidenceId || item.packageItemId}</strong>
                <p>{(item.nativeControlKey || item.controlKey) ? `${item.nativeControlKey || item.controlKey}${(item.questionId || item.questionKey) ? ` · ${item.questionId || item.questionKey}` : ""}` : (item.questionId || item.questionKey || "—")}</p>
                <p>{item.type || item.evidenceType || "—"} · {item.source || item.evidenceSource || "—"}</p>
                <p>{item.verificationStatus || "—"} · {item.freshnessStatus || item.freshness || "—"}</p>
                <p>Scope: IS {(item.informationSystemIds || item.scopeContext?.informationSystemIds || []).length} · Assets {(item.assetIds || item.scopeContext?.assetIds || []).length}</p>
                <p>{item.included === false ? "Excluded" : "Included"} · {item.disclosureApproved ? "Disclosure approved" : "Not approved for disclosure"}</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    type="button"
                    size="small"
                    color="secondary"
                    disabled={actionLoading !== null || manifestApproved}
                    onClick={() => void runAction(`manifest-item-${item.packageItemId || item.evidenceId}-include`, () => fetch(
                      API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_MANIFEST_ITEM(workflowId, item.manifestItemId || item.packageItemId || item.evidenceId),
                      { method: "PATCH", headers, body: JSON.stringify({ included: true }) },
                    ), "certification.workflowDetail.success.manifestBuilt")}
                  >
                    Include
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    color="outline"
                    disabled={actionLoading !== null || manifestApproved}
                    onClick={() => void runAction(`manifest-item-${item.packageItemId || item.evidenceId}-exclude`, () => fetch(
                      API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_MANIFEST_ITEM(workflowId, item.manifestItemId || item.packageItemId || item.evidenceId),
                      { method: "PATCH", headers, body: JSON.stringify({ included: false }) },
                    ), "certification.workflowDetail.success.manifestBuilt")}
                  >
                    Exclude
                  </Button>
                  {item.policyId || item.policyVersionId ? (
                    <Button type="button" size="small" color="outline" onClick={() => navigate("/governance/policies")}>
                      Open policy
                    </Button>
                  ) : null}
                  {item.evidenceId ? (
                    <Button type="button" size="small" color="outline" onClick={() => navigate("/governance/evidence")}>
                      Open
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          {!(manifest?.items || []).length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.emptyEvidence", "No evidence manifest items.")}</p> : null}
        </GovernanceListingSection>
      ) : null}

      {tab === "evidenceRequests" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.evidenceRequests", "Evidence Requests")}>
          {renderMessage()}
          <div className={listStyles.listStack}>
            {evidenceRequests.map((item: any) => {
              const meta = item?.metadata && typeof item.metadata === "object" ? item.metadata : {};
              const controlKey = item.nativeControlKey || item.controlCode || meta.nativeControlKey || "";
              const questionKey = item.questionId || item.questionKey || meta.questionKey || "";
              const questionTitle = item.questionTitle || meta.questionTitle || "";
              const evidenceType = String(item.evidenceType || meta.evidenceType || "OTHER").toUpperCase();
              const legacyUnlinked = Boolean(item.legacyUnlinked) || !controlKey;
              const openForLink = ["open", "responded"].includes(String(item.status || "").toLowerCase());
              return (
              <div key={item.id} className={listStyles.panelCard}>
                <strong>{item.title || item.id}</strong>
                <p>{t(`certification.status.request.${String(item.status || "open").toUpperCase()}`, String(item.status || "open").replaceAll("_", " "))}</p>
                <p>{t("certification.workflowDetail.control", "Control")}: {controlKey || t("certification.workflowDetail.legacyUnlinkedControl", "Not linked")}</p>
                <p>{t("certification.workflowDetail.linkedQuestion", "Linked question")}: {questionKey ? `${questionKey}${questionTitle ? ` · ${questionTitle}` : ""}` : t("certification.workflowDetail.legacyUnlinkedQuestion", "Not linked")}</p>
                <p>{t("certification.workflowDetail.evidenceType", "Evidence type")}: {t(`certification.evidenceTypes.${evidenceType}`, t(`governance.evidenceArtifacts.category.${evidenceType}`, evidenceType))}</p>
                {legacyUnlinked ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.legacyRequest", "Legacy request — control not linked")}</p> : null}
                {legacyUnlinked && openForLink && !terminal ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <Select
                      label={t("certification.workflowDetail.control", "Control")}
                      value={legacyLinkDraft[item.id] || null}
                      onChange={(value) => setLegacyLinkDraft((prev) => ({ ...prev, [item.id]: String(value || "") }))}
                      placeholder={t("certification.workflowDetail.control", "Control")}
                      options={controlReviews.filter((row: any) => !row.preview).map((row: any) => ({
                        value: row.controlKey,
                        label: `${row.controlKey}${row.title ? ` · ${row.title}` : ""}`,
                      }))}
                    />
                    <Button
                      type="button"
                      size="small"
                      color="secondary"
                      disabled={actionLoading !== null || !legacyLinkDraft[item.id]}
                      onClick={() => void runAction(`linkLegacy-${item.id}`, () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_REQUEST_LINK(workflowId, item.id), {
                        method: "PATCH",
                        headers,
                        body: JSON.stringify({ nativeControlKey: legacyLinkDraft[item.id] }),
                      }), "certification.workflowDetail.success.legacyLinked")}
                    >
                      {t("certification.workflowDetail.linkLegacy", "Link control")}
                    </Button>
                  </div>
                ) : null}
                <TextField
                  multiline
                  rows={3}
                  label={t("certification.workflowDetail.responseComment", "Response comment")}
                  value={evidenceResponseDraft[item.id] || ""}
                  onChange={(value) => setEvidenceResponseDraft((prev) => ({ ...prev, [item.id]: value }))}
                  placeholder={t("certification.workflowDetail.responseComment", "Response comment")}
                  disabled={terminal}
                />
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 8 }}>
                  <Select
                    label={t("certification.workflowDetail.selectExistingEvidence", "Select existing Evidence")}
                    value={linkedEvidenceDraft[item.id] || null}
                    onChange={(value) => setLinkedEvidenceDraft((prev) => ({ ...prev, [item.id]: String(value || "") }))}
                    placeholder={t("certification.workflowDetail.none", "None")}
                    disabled={terminal}
                    options={hubEvidence.map((ev: any) => ({
                      value: ev.id,
                      label: `${ev.title || ev.id} · ${t(`certification.status.verification.${String(ev.verificationStatus || "PENDING").toUpperCase()}`, ev.verificationStatus || "PENDING")}`,
                    }))}
                  />
                  <FilePicker
                    value={evidenceFiles[item.id] || null}
                    onChange={(file) => setEvidenceFiles((prev) => ({ ...prev, [item.id]: file }))}
                    chooseLabel={t("certification.workflowDetail.uploadEvidence", "Upload evidence file")}
                    noFileLabel={t("certification.workflowDetail.noFileSelected", "No file selected")}
                    disabled={terminal}
                  />
                  <Button
                    type="button"
                    size="small"
                    color="outline"
                    disabled={actionLoading !== null || terminal}
                    onClick={() => setUnavailableDraft((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                  >
                    {unavailableDraft[item.id]
                      ? t("certification.workflowDetail.evidenceAvailable", "Evidence available")
                      : t("certification.workflowDetail.evidenceUnavailable", "Evidence unavailable")}
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    color="primary"
                    disabled={actionLoading !== null || terminal}
                    onClick={() => void runAction(
                      `evidenceRequest-${item.id}`,
                      () => {
                        const form = new FormData();
                        form.append("comment", evidenceResponseDraft[item.id] || "");
                        form.append("status", unavailableDraft[item.id] ? "unavailable" : "responded");
                        form.append("unavailable", unavailableDraft[item.id] ? "true" : "false");
                        form.append("evidenceType", evidenceType);
                        form.append("artifactCategory", evidenceType);
                        if (linkedEvidenceDraft[item.id]) form.append("linkedEvidenceId", linkedEvidenceDraft[item.id]);
                        const file = evidenceFiles[item.id];
                        if (file) form.append("file", file);
                        return fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_EVIDENCE_REQUEST_RESPOND(workflowId, item.id), {
                          method: "POST",
                          headers: authHeaders,
                          body: form,
                        });
                      },
                      "certification.workflowDetail.success.evidenceRequestResponded",
                    )}
                  >
                    {actionLoading === `evidenceRequest-${item.id}` ? t("common.saving", "Saving...") : t("certification.workflowDetail.respondRequest", "Submit response")}
                  </Button>
                </div>
              </div>
              );
            })}
            {!evidenceRequests.length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.emptyEvidenceRequests", "No evidence requests.")}</p> : null}
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === "findings" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.findings", "Findings")}>
          {renderMessage()}
          <div className={listStyles.listStack}>
            {findings.map((finding) => (
              <div key={finding.id} className={listStyles.panelCard}>
                <strong>{finding.title}</strong>
                <p>{t(`certification.findingTypes.${String(finding.findingType || finding.type || "OBSERVATION").toUpperCase()}`, finding.findingType || finding.type || "OBSERVATION")} · {t(`certification.status.finding.${String(finding.status || "OPEN").toUpperCase()}`, finding.status || "OPEN")}</p>
                <p>{finding.controlKey || "—"}</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button type="button" size="small" color="primary" disabled={actionLoading !== null || terminal} onClick={() => void runAction(`findingAccept-${finding.id}`, () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_FINDING(workflowId, finding.id), { method: "PATCH", headers, body: JSON.stringify({ status: "ACCEPTED_RESPONSE" }) }), "certification.workflowDetail.success.findingUpdated")}>
                    {actionLoading === `findingAccept-${finding.id}` ? t("common.saving", "Saving...") : t("certification.workflowDetail.acceptResponse", "Accept response")}
                  </Button>
                  <Button type="button" size="small" color="secondary" disabled={actionLoading !== null} onClick={() => void runAction(`findingDispute-${finding.id}`, () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_FINDING(workflowId, finding.id), { method: "PATCH", headers, body: JSON.stringify({ status: "DISPUTED" }) }), "certification.workflowDetail.success.findingUpdated")}>
                    {actionLoading === `findingDispute-${finding.id}` ? t("common.saving", "Saving...") : t("certification.workflowDetail.disputeFinding", "Dispute")}
                  </Button>
                  <Button type="button" size="small" color="outline" disabled={actionLoading !== null} onClick={() => void runAction(`findingClose-${finding.id}`, () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_FINDING(workflowId, finding.id), { method: "PATCH", headers, body: JSON.stringify({ status: "CLOSED" }) }), "certification.workflowDetail.success.findingUpdated")}>
                    {actionLoading === `findingClose-${finding.id}` ? t("common.saving", "Saving...") : t("certification.workflowDetail.closeFinding", "Close")}
                  </Button>
                </div>
              </div>
            ))}
            {!findings.length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.emptyFindings", "No findings.")}</p> : null}
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === "remediation" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.remediation", "Remediation")}>
          <div className={listStyles.listStack}>
            {findings.filter((finding) => Boolean(finding.remediationId)).map((finding) => (
              <div key={finding.id} className={listStyles.panelCard}>
                <strong>{finding.title}</strong>
                <p>{t("certification.workflowDetail.remediationId", "Remediation ID")}: {finding.remediationId}</p>
              </div>
            ))}
            {!findings.some((finding) => Boolean(finding.remediationId)) ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.emptyRemediation", "No remediation linked to findings.")}</p> : null}
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === "auditors" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.auditors", "Auditors")}>
          {renderMessage()}
          <div className={listStyles.panelCard}>
            <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.inviteHint", "Invitation grants portal access after baseline, scope, evidence manifest, and package prerequisites are complete. Do not select systems here.")}</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <TextField
                label={t("certification.workflowDetail.auditorEmail", "Auditor email")}
                value={invitationDraft.auditorEmail}
                onChange={(value) => setInvitationDraft((prev: any) => ({ ...prev, auditorEmail: value }))}
                placeholder={t("certification.workflowDetail.auditorEmail", "Auditor email")}
              />
              <TextField
                label={t("certification.workflowDetail.auditorName", "Auditor name")}
                value={invitationDraft.auditorName}
                onChange={(value) => setInvitationDraft((prev: any) => ({ ...prev, auditorName: value }))}
                placeholder={t("certification.workflowDetail.auditorName", "Auditor name")}
              />
              <Select
                label={t("certification.workflowDetail.auditorRole", "Auditor role")}
                value={invitationDraft.auditorRole}
                onChange={(value) => setInvitationDraft((prev: any) => ({ ...prev, auditorRole: String(value || "LEAD_AUDITOR") }))}
                options={["LEAD_AUDITOR", "AUDITOR", "REVIEWER", "OBSERVER"].map((role) => ({
                  value: role,
                  label: t(`certification.roles.${role}`, role.replaceAll("_", " ")),
                }))}
              />
              <Select
                label={t("certification.workflowDetail.invitationLocale", "Invitation language")}
                value={invitationDraft.invitationLocale}
                onChange={(value) => setInvitationDraft((prev: any) => ({ ...prev, invitationLocale: String(value || "en") }))}
                options={["en", "ru", "es"].map((locale) => ({ value: locale, label: locale.toUpperCase() }))}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Button
                type="button"
                size="small"
                color="primary"
                disabled={actionLoading !== null || !canInviteAuditor}
                onClick={() => void runAction("inviteAuditor", async () => {
                  const response = await fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_AUDITOR_TOKEN(workflowId), {
                    method: "POST",
                    headers,
                    body: JSON.stringify({
                      ...invitationDraft,
                      certificationPackageId: packageId,
                    }),
                  });
                  const json = await response.json().catch(() => ({}));
                  if (response.ok) {
                    const absoluteUrl = toAuditorPortalPageUrl(json?.data?.portalUrl);
                    if (absoluteUrl) {
                      setLastPortalUrl(absoluteUrl);
                      await navigator.clipboard.writeText(absoluteUrl).catch(() => null);
                    }
                  }
                  return new Response(JSON.stringify(json), { status: response.status, headers: { "Content-Type": "application/json" } });
                }, "certification.workflowDetail.success.invitationSent")}
              >
                {actionLoading === "inviteAuditor" ? t("common.saving", "Saving...") : t("certification.workflowDetail.inviteAuditor", "Invite auditor")}
              </Button>
              {!canInviteAuditor && invitationDraft.auditorEmail ? (
                <span className={listStyles.panelSubtitle}>
                  {inviteBlockers.length
                    ? inviteBlockers.map((blocker: any) => t(`certification.workflowDetail.blockers.${String(blocker.code || blocker)}`, String(blocker.code || blocker))).join(" · ")
                    : !packageId
                      ? t("certification.workflowDetail.blockers.PACKAGE_MISSING", "Certification package is missing")
                      : t("certification.workflowDetail.inviteHint", "Invitation grants portal access after baseline, scope, evidence manifest, and package prerequisites are complete. Do not select systems here.")}
                </span>
              ) : null}
              <Button type="button" size="small" color="danger" disabled={actionLoading !== null} onClick={() => void runAction("revokeAuditor", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_AUDITOR_TOKEN_REVOKE(workflowId), { method: "POST", headers }), "certification.workflowDetail.success.auditorRevoked")}>
                {actionLoading === "revokeAuditor" ? t("common.saving", "Saving...") : t("certification.workflowDetail.revokeAuditor", "Revoke auditor")}
              </Button>
            </div>
            {lastPortalUrl ? (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                <p>{t("certification.workflowDetail.success.invitationSent", "Invitation sent")}</p>
                <TextField
                  readOnly
                  label={t("certification.workflowDetail.portalUrlLabel", "Auditor portal link")}
                  value={lastPortalUrl}
                  onChange={() => undefined}
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    type="button"
                    size="small"
                    color="secondary"
                    onClick={async () => {
                      await navigator.clipboard.writeText(lastPortalUrl).catch(() => null);
                      setActionSuccess(t("certification.workflowDetail.success.linkCopied", "Portal link copied"));
                    }}
                  >
                    {t("certification.workflowDetail.copyPortalUrl", "Copy portal URL")}
                  </Button>
                  <a href={lastPortalUrl} target="_blank" rel="noreferrer">
                    <Button type="button" size="small" color="outline">
                      {t("certification.workflowDetail.openPortalUrl", "Open portal link")}
                    </Button>
                  </a>
                </div>
              </div>
            ) : null}
          </div>
          <div className={listStyles.listStack}>
            {(detail?.auditors || []).map((auditor: any) => (
              <div key={auditor.id} className={listStyles.panelCard}>
                <strong>{auditor.name || auditor.email}</strong>
                <p>{t(`certification.roles.${String(auditor.role || "AUDITOR").toUpperCase()}`, auditor.role || "AUDITOR")}</p>
                <p>{auditor.expiresAt || "—"}</p>
                <p>{auditor.revoked ? t("certification.workflowDetail.revoked", "Revoked") : t("certification.workflowDetail.active", "Active")}</p>
              </div>
            ))}
            {!detail?.auditors?.length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.emptyAuditors", "No auditor assigned.")}</p> : null}
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === "package" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.package", "Package")}>
          {renderMessage()}
          <p>{t("certification.workflowDetail.packageId", "Package ID")}: {packageId || "—"}</p>
          <p>{t("certification.workflowDetail.packageVersion", "Package version")}: {packagePrepared ? manifest?.packageVersion : t("certification.workflowDetail.notPrepared", "Not prepared")}</p>
          <p>{t("certification.workflowDetail.packageStatus", "Package status")}: {packageStatus || "—"}</p>
          <p>{t("certification.workflowDetail.baselineVersion", "Baseline version")}: {baseline?.baselineVersion || 0}</p>
          <p>{t("certification.workflowDetail.scopeVersion", "Scope version")}: {scope?.scopeVersion || 0}</p>
          <p>{t("certification.workflowDetail.manifestVersion", "Evidence manifest version")}: {manifest?.evidenceManifestVersion || 0}</p>
          {!canFreezePackage && packageFreezeBlockers.length ? (
            <p className={listStyles.panelSubtitle}>{packageFreezeBlockers.join(" · ")}</p>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button type="button" size="small" color="primary" disabled={actionLoading !== null || terminal || !canGeneratePackage} onClick={() => void ensureAndGeneratePackage()}>
              {actionLoading === "packageGenerate" ? t("common.generating", "Generating...") : t("certification.workflowDetail.generatePackage", "Generate package")}
            </Button>
            <Button type="button" size="small" color={canFreezePackage ? "primary" : "secondary"} disabled={actionLoading !== null || terminal || !canFreezePackage} onClick={() => void runAction("packageFreeze", () => fetch(`${API_ENDPOINTS.COMPLIANCE_CERTIFICATION_PACKAGES}/${packageId}/freeze`, { method: "POST", headers }), "certification.workflowDetail.success.packageFrozen")}>
              {actionLoading === "packageFreeze" ? t("common.saving", "Saving...") : t("certification.workflowDetail.freezePackage", "Freeze package")}
            </Button>
            <Button type="button" size="small" color="secondary" disabled={actionLoading !== null || terminal || !packageId} onClick={() => void runAction("packageApprove", () => fetch(`${API_ENDPOINTS.COMPLIANCE_CERTIFICATION_PACKAGES}/${packageId}/approve`, { method: "POST", headers }), "certification.workflowDetail.success.packageApproved")}>
              {actionLoading === "packageApprove" ? t("common.saving", "Saving...") : t("certification.workflowDetail.approvePackage", "Approve package")}
            </Button>
            <Button type="button" size="small" color="outline" disabled={actionLoading !== null || terminal} onClick={() => void runAction("packageUpdate", () => fetch(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOW_PACKAGE_UPDATE_PROPOSALS(workflowId), { method: "POST", headers, body: JSON.stringify({ reason: "operator_update" }) }), "certification.workflowDetail.success.packageUpdateProposed")}>
              {actionLoading === "packageUpdate" ? t("common.saving", "Saving...") : t("certification.workflowDetail.proposePackageUpdate", "Propose package update")}
            </Button>
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === "decision" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.decision", "Decision")}>
          {(detail?.decisions || []).map((decision: any) => (
            <div key={decision.id} className={listStyles.panelCard}>
              <strong>{decision.decision}</strong>
              <p>{decision.decisionDate}</p>
              <p>{decision.decidedByEmail || "—"}</p>
            </div>
          ))}
          {!detail?.decisions?.length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.emptyDecision", "No decision recorded.")}</p> : null}
        </GovernanceListingSection>
      ) : null}

      {tab === "timeline" ? (
        <GovernanceListingSection title={t("certification.workflowDetail.tabs.timeline", "Timeline")}>
          <div className={listStyles.listStack}>
            {timeline.map((event: any) => (
              <div key={event.id} className={listStyles.panelCard}>
                <strong>{event.title}</strong>
                <p>{eventTypeLabel(event.eventType)}</p>
                <p>{event.createdAt}</p>
              </div>
            ))}
            {!timeline.length ? <p className={listStyles.panelSubtitle}>{t("certification.workflowDetail.emptyTimeline", "No timeline events.")}</p> : null}
          </div>
        </GovernanceListingSection>
      ) : null}
    </div>
  );
}
